import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { getStaffRequests } from '../../../../api/staff-requests/get-staff-requests'
import { useObserver } from '../../../../lib/contexts/observer'
import useTranslate from '../../../../lib/hooks/use-translate'
import { staffQualificationEnumMapping } from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { StaffRequestProperties } from '../../../../lib/types/models/staff-request/properties.staff-request'
import { StaffRequest } from '../../../../lib/types/models/staff-request/staff-request'
import InformationToolTip from '../../../elements/information-tooltip'

type Props = {
  centerId: number
}

const CenterGlobalViewStaffRequestSection = ({ centerId }: Props) => {
  const { t } = useTranslate()
  const queryClient = useQueryClient()

  const { data: staffRequests } = useQuery({
    queryKey: ['staff-requests', 'centerId', centerId],
    queryFn: () => {
      return getStaffRequests({ filter: { centerIds: [centerId] } })
    }
  })

  const coursesStaffRequests = StaffRequestProperties.getCoursesStaffRequests(
    staffRequests?.data ?? []
  )
  const activitiesStaffRequests = StaffRequestProperties.getActivitiesStaffRequests(
    staffRequests?.data ?? []
  )

  useObserver({
    key: 'refresh-staff-request',
    update: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-requests', 'centerId', centerId] })
    }
  })

  const columns = (type: 'courses' | 'activities'): Column<StaffRequest>[] => [
    {
      accessorKey: 'qualification',
      header: type === 'courses' ? 'Cours' : 'Activités',
      cell: ({ row }) => {
        const { type, label } = staffQualificationEnumMapping[row.original.qualification]
        const numberOfStaff = row.original.numberOfStaff

        return (
          <Badge
            variant="white"
            textColor={
              type === 'french' ? tailwindColors.fushia.DEFAULT : tailwindColors.purple.DEFAULT
            }
            text={`${t(label)} : ${numberOfStaff}`}
          />
        )
      }
    }
  ]

  return (
    <div className="flex flex-col gap-3 rounded-xl">
      <div className="flex gap-2 items-center">
        <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
          Demandes d'encadrants
        </Typography>
        <InformationToolTip>
          <Typography variant="label" color={tailwindColors.white.active} className="w-64">
            Les tableaux ci-dessous présentent les encadrants filtrés par cours et par activités.
            Les qualifications, telles que l’« AAT » ou d’autres, sont exclues des tableaux.
          </Typography>
        </InformationToolTip>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <DataTable
          bodyVariant="white"
          headerClassName="bg-white-active"
          columns={columns('courses')}
          data={coursesStaffRequests}
          isLoading={false}
          rowCount={0}
          paginated={false}
        />

        <DataTable
          bodyVariant="white"
          headerClassName="bg-white-active"
          tableClassName="border-grey-light"
          columns={columns('activities')}
          data={activitiesStaffRequests}
          isLoading={false}
          rowCount={0}
          paginated={false}
        />
      </div>
    </div>
  )
}

export default CenterGlobalViewStaffRequestSection
